/**
 * <aya-anchor-nav> — in-page jump links for the treatment detail template.
 *
 * Progressive enhancement only. Without JS the links are plain anchors and
 * still work; this adds smooth scrolling that respects reduced-motion, and
 * marks the link for the section currently in view as current.
 */
class AyaAnchorNav extends HTMLElement {
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll('.aya-anchor-nav__link'));
    if (!this.links.length) return;

    this.targets = this.links
      .map((link) => {
        const id = (link.getAttribute('href') || '').replace(/^#/, '');
        const el = id ? document.getElementById(id) : null;
        return el ? { link, el } : null;
      })
      .filter(Boolean);

    this.onClick = this.onClick.bind(this);
    this.links.forEach((link) => link.addEventListener('click', this.onClick));

    this.observe();
  }

  disconnectedCallback() {
    this.links?.forEach((link) => link.removeEventListener('click', this.onClick));
    this.observer?.disconnect();
  }

  onClick(event) {
    const id = (event.currentTarget.getAttribute('href') || '').replace(/^#/, '');
    const target = id ? document.getElementById(id) : null;
    if (!target) return; // let the browser handle a dead link normally

    event.preventDefault();

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });

    // Keep the URL shareable and move focus for keyboard/screen-reader users.
    if (window.history?.replaceState) {
      window.history.replaceState(null, '', `#${id}`);
    }
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  observe() {
    if (!('IntersectionObserver' in window) || !this.targets.length) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const match = this.targets.find((t) => t.el === entry.target);
          if (!match) return;
          this.targets.forEach((t) => {
            const isCurrent = t === match;
            t.link.classList.toggle('is-current', isCurrent);
            if (isCurrent) {
              t.link.setAttribute('aria-current', 'true');
            } else {
              t.link.removeAttribute('aria-current');
            }
          });
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    this.targets.forEach((t) => this.observer.observe(t.el));
  }
}

if (!customElements.get('aya-anchor-nav')) {
  customElements.define('aya-anchor-nav', AyaAnchorNav);
}
